import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FolderOpen, Plus, Pencil, Target } from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { getPrograms } from '@/lib/firebase';
import { Link } from 'wouter';
import { logger } from '@/lib/logger';
import ProgramMilestonesModal from '@/components/program-milestones-modal';

interface Program {
  id: string;
  name: string;
  module: string;
  contentLibrary: string;
  programType: string;
  enabled: boolean;
}

export default function Programs() {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [milestonesModalOpen, setMilestonesModalOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const { user } = useAuth();

  const handleOpenMilestones = (program: Program) => {
    setSelectedProgram(program);
    setMilestonesModalOpen(true);
  };

  useEffect(() => {
    if (!user) return;
    const loadData = async () => {
      try {
        logger.log('📁 Loading programs...');
        const data = await getPrograms();
        logger.log('📋 Programs loaded:', data.length);
        setPrograms(data as Program[]);
      } catch (error: any) {
        logger.error('❌ Error loading programs:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
    const timer = setInterval(loadData, 15_000);
    return () => clearInterval(timer);
  }, [user]);

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-center text-slate-600">Loading programs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <FolderOpen className="w-8 h-8 text-primary" />
              <h1 className="text-3xl font-bold text-slate-900">Programs</h1>
            </div>
            <Link href="/programs/add">
              <Button data-testid="button-add-program" className="flex items-center space-x-2">
                <Plus className="w-4 h-4" />
                <span>Add Program</span>
              </Button>
            </Link>
          </div>
          <p className="text-slate-600">
            Manage and view all programs available in the system.
          </p>
        </div>

        {/* Programs Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <FolderOpen className="w-5 h-5" />
              <span>All Programs ({programs.length})</span>
            </CardTitle>
            <CardDescription>
              List of all programs with their details and status.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {programs.length === 0 ? (
              <div className="text-center py-12">
                <FolderOpen className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No programs found</h3>
                <p className="text-slate-600">There are no programs in the system yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Module</TableHead>
                      <TableHead>Library</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {programs.map((program) => (
                      <TableRow 
                        key={program.id} 
                        data-testid={`row-program-${program.id}`}
                        className="hover:bg-slate-50"
                      >
                        <TableCell className="font-medium" data-testid={`text-name-${program.id}`}>
                          {program.name}
                        </TableCell>
                        <TableCell data-testid={`text-module-${program.id}`}>
                          {program.module && (
                            <Badge variant="outline" className="capitalize">
                              {program.module}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell data-testid={`text-library-${program.id}`}>
                          {program.contentLibrary || '-'}
                        </TableCell>
                        <TableCell data-testid={`text-type-${program.id}`}>
                          {program.programType && (
                            <Badge variant="secondary" className="capitalize">
                              {program.programType}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell data-testid={`status-enabled-${program.id}`}>
                          <Badge 
                            variant={program.enabled ? "default" : "destructive"}
                          >
                            {program.enabled ? "Enabled" : "Disabled"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenMilestones(program)}
                            data-testid={`button-milestones-${program.id}`}
                          >
                            <Target className="w-4 h-4 mr-2" />
                            Milestones
                          </Button>
                          <Link href={`/programs/edit/${program.id}`}>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`button-edit-${program.id}`}
                            >
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {selectedProgram && (
        <ProgramMilestonesModal
          open={milestonesModalOpen}
          onOpenChange={(open) => {
            setMilestonesModalOpen(open);
            if (!open) setSelectedProgram(null);
          }}
          programId={selectedProgram.id}
          programName={selectedProgram.name}
        />
      )}
    </div>
  );
}